import React, { useState } from 'react';
import { Button, List, Typography, Modal, Form, Input, Select, Checkbox, Space, InputNumber, Switch, Alert, } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Scenarios } from '@whatsper/texterchat-common';
import { Trans, useTranslation } from 'react-i18next';
import { SCENARIO_THEME } from './scenarioTheme';
import ParameterEditor from './ParameterEditor';
const { LoopType, LoopForeachMode } = Scenarios.Components;
const foreachHandler = {
    populateForm(loop, form) {
        var _a, _b, _c;
        const isSequential = loop.foreachMode === LoopForeachMode.sequential;
        form.setFieldsValue({
            foreachMode: loop.foreachMode,
            as: loop.as,
            confidentialData: loop.confidentialData || false,
            itemDelay: isSequential ? ((_a = loop.itemDelay) !== null && _a !== void 0 ? _a : 0) : 0,
            breakOnError: isSequential ? ((_b = loop.breakOnError) !== null && _b !== void 0 ? _b : false) : false,
            concurency: !isSequential ? ((_c = loop.concurency) !== null && _c !== void 0 ? _c : 1) : 1,
        });
        return loop.input;
    },
    buildLoop(values, input) {
        var _a, _b, _c;
        const { foreachMode, as, confidentialData } = values;
        const base = {
            type: LoopType.foreach,
            as,
            input,
            confidentialData: confidentialData || false,
        };
        if (foreachMode === LoopForeachMode.sequential) {
            return Object.assign(Object.assign({}, base), { foreachMode: LoopForeachMode.sequential, itemDelay: ((_a = values.itemDelay) !== null && _a !== void 0 ? _a : 0), breakOnError: ((_b = values.breakOnError) !== null && _b !== void 0 ? _b : false) });
        }
        return Object.assign(Object.assign({}, base), { foreachMode: LoopForeachMode.parallel, concurency: ((_c = values.concurency) !== null && _c !== void 0 ? _c : 1) });
    },
    renderFormFields(selectedMode, editingLoopInfo, allUsedNames, t) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Form.Item, { name: "foreachMode", label: t('foreachMode'), rules: [{ required: true, message: t('foreachModeRequired') }] },
                React.createElement(Select, { placeholder: t('foreachModePlaceholder') },
                    React.createElement(Select.Option, { value: LoopForeachMode.sequential }, t('foreachModeSequential')),
                    React.createElement(Select.Option, { value: LoopForeachMode.parallel }, t('foreachModeParallel')))),
            React.createElement(Form.Item, { name: "as", label: t('loopAs'), rules: [
                    { required: true, message: t('loopAsRequired') },
                    {
                        pattern: /^\w+$/,
                        message: t('loopAsPatternError'),
                    },
                    {
                        validator: (_, value) => {
                            const editingLoop = editingLoopInfo === null || editingLoopInfo === void 0 ? void 0 : editingLoopInfo.data.loop;
                            const originalAs = (editingLoop === null || editingLoop === void 0 ? void 0 : editingLoop.type) === LoopType.foreach ? editingLoop.as : undefined;
                            if (!value || value === originalAs || !allUsedNames.includes(value)) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('loopAsNameTaken')));
                        },
                    },
                ], help: t('loopAsHelp') },
                React.createElement(Input, { placeholder: t('loopAsPlaceholder') })),
            React.createElement(Form.Item, { name: "confidentialData", valuePropName: "checked", style: { marginBottom: 16, marginTop: 16 } },
                React.createElement(Checkbox, null,
                    React.createElement(Trans, { i18nKey: "containsConfidentialData" }),
                    React.createElement(Typography.Text, { type: "secondary", style: { display: 'block', fontSize: '12px' } },
                        React.createElement(Trans, { i18nKey: "containsConfidentialDataTooltip" })))),
            selectedMode === LoopForeachMode.sequential && (React.createElement(React.Fragment, null,
                React.createElement(Form.Item, { name: "itemDelay", label: t('loopItemDelay') },
                    React.createElement(InputNumber, { min: 0, style: { width: '100%' } })),
                React.createElement(Form.Item, { name: "breakOnError", label: t('loopBreakOnError'), valuePropName: "checked" },
                    React.createElement(Switch, null)))),
            selectedMode === LoopForeachMode.parallel && (React.createElement(Form.Item, { name: "concurency", label: t('loopConcurrency') },
                React.createElement(InputNumber, { min: 1, style: { width: '100%' } })))));
    },
    getDisplayText(loop) {
        return (React.createElement(Typography.Text, { strong: true },
            React.createElement(Trans, { i18nKey: "loopForeachDisplay", values: { mode: loop.foreachMode, name: loop.as }, defaults: "{{mode}} foreach \u2013 as: '{{name}}'" })));
    },
};
const LOOP_TYPE_HANDLERS = {
    [LoopType.foreach]: foreachHandler,
};
// Wrap input as a single required property so ParameterEditor's buildUiSchema
const INPUT_SCHEMA_BASE = {
    type: 'object',
    required: ['value'],
    properties: { value: {} },
};
const { Title } = Typography;
const Step_Loops = ({ loops, onLoopsChange, availableProviders, position, allUsedNames }) => {
    var _a;
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingLoopInfo, setEditingLoopInfo] = useState(null);
    const [form] = Form.useForm();
    const [inputValue, setInputValue] = useState(undefined);
    const inputSchema = Object.assign(Object.assign({}, INPUT_SCHEMA_BASE), { properties: { value: { title: t('loopInputIterable') } } });
    const theme = SCENARIO_THEME.loops;
    const IconComponent = theme.icon;
    const isEditing = editingLoopInfo !== null;
    const openAddModal = () => {
        setEditingLoopInfo(null);
        setInputValue(undefined);
        form.resetFields();
        setIsModalVisible(true);
    };
    const openEditModal = (instance, index) => {
        const handler = LOOP_TYPE_HANDLERS[instance.loop.type];
        if (!handler)
            return;
        setEditingLoopInfo({ index, data: instance });
        const loopInput = handler.populateForm(instance.loop, form);
        setInputValue(loopInput);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
    };
    const handleOk = () => {
        form.validateFields().then(values => {
            var _a;
            const loopType = (_a = values.loopType) !== null && _a !== void 0 ? _a : LoopType.foreach;
            const handler = LOOP_TYPE_HANDLERS[loopType];
            if (!handler || inputValue === undefined)
                return;
            const loop = handler.buildLoop(values, inputValue);
            const newInstance = { loop, position };
            const newLoops = [...loops];
            if (editingLoopInfo !== null) {
                newLoops[editingLoopInfo.index] = newInstance;
            }
            else {
                newLoops.push(newInstance);
            }
            onLoopsChange(newLoops);
            handleCancel();
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };
    const handleRemoveLoop = (index) => {
        const newLoops = [...loops];
        newLoops.splice(index, 1);
        onLoopsChange(newLoops);
    };
    const selectedForeachMode = Form.useWatch('foreachMode', form);
    const isOkDisabled = inputValue === undefined;
    const editingLoopType = (_a = editingLoopInfo === null || editingLoopInfo === void 0 ? void 0 : editingLoopInfo.data.loop.type) !== null && _a !== void 0 ? _a : LoopType.foreach;
    const loopTypeDisplayName = editingLoopType.charAt(0).toUpperCase() + editingLoopType.slice(1);
    const activeHandler = LOOP_TYPE_HANDLERS[editingLoopType];
    return (React.createElement("div", null,
        React.createElement(Space, { style: { marginBottom: 24 } },
            React.createElement(IconComponent, { style: { fontSize: '20px', color: theme.color } }),
            React.createElement(Title, { level: 4, style: { margin: 0, color: theme.color } }, theme.name)),
        React.createElement(Typography.Paragraph, { style: { marginBottom: 24, color: '#666' } },
            React.createElement(Trans, { i18nKey: "loopsWrapSteps" })),
        React.createElement(List, { bordered: true, dataSource: loops, renderItem: (instance, index) => {
                const { loop } = instance;
                const handler = LOOP_TYPE_HANDLERS[loop.type];
                return (React.createElement(List.Item, { actions: [
                        React.createElement(Button, { type: "link", onClick: () => openEditModal(instance, index) },
                            React.createElement(Trans, { i18nKey: "editLoop" })),
                        React.createElement(Button, { type: "link", danger: true, onClick: () => handleRemoveLoop(index) },
                            React.createElement(Trans, { i18nKey: "removeLoop" })),
                    ] },
                    React.createElement("div", null,
                        handler ? (handler.getDisplayText(loop)) : (React.createElement(Typography.Text, { type: "secondary" },
                            React.createElement(Trans, { i18nKey: "loopTypeUnsupported", values: { loopType: loop.type } }))),
                        loop.confidentialData && (React.createElement(Typography.Text, { type: "secondary", style: { marginLeft: 8 } },
                            "[",
                            React.createElement(Trans, { i18nKey: "confidential" }),
                            "]")))));
            } }),
        React.createElement(Button, { type: "dashed", onClick: openAddModal, style: { marginTop: '16px' } },
            React.createElement(PlusOutlined, null),
            " ",
            React.createElement(Trans, { i18nKey: "addLoop" })),
        React.createElement(Modal, { title: isEditing ? t('editLoopModalTitle', { loopType: loopTypeDisplayName }) : t('addLoopModalTitle', { loopType: loopTypeDisplayName }), open: isModalVisible, onOk: handleOk, onCancel: handleCancel, okButtonProps: { disabled: isOkDisabled }, okText: isEditing ? t('saveLoop') : t('addLoop'), destroyOnClose: true, width: 700, bodyStyle: { maxHeight: '70vh', overflowY: 'auto' } },
            React.createElement(Alert, { message: t('aboutForeachLoops'), description: t('foreachLoopDescription'), type: "info", showIcon: true, style: { marginBottom: 16 } }),
            React.createElement(Form, { form: form, layout: "vertical", name: "loop_form_in_modal", initialValues: {
                    foreachMode: LoopForeachMode.sequential,
                    confidentialData: false,
                    itemDelay: 0,
                    breakOnError: false,
                    concurency: 1,
                } }, activeHandler
                ? activeHandler.renderFormFields(selectedForeachMode, editingLoopInfo, allUsedNames, t)
                : foreachHandler.renderFormFields(selectedForeachMode, editingLoopInfo, allUsedNames, t)),
            React.createElement("div", { style: { marginTop: 16 } },
                React.createElement(ParameterEditor, { schema: inputSchema, formData: { value: inputValue }, onChange: (data) => setInputValue(data === null || data === void 0 ? void 0 : data.value), availableProviders: availableProviders })))));
};
export default Step_Loops;
